<template>
    <div class="page page-quotes page-quote-payment">

        <app-modal
            v-if="isProtectionDetailsOpen"
            class="quote-policy-details"
            hasCloseButton
            small
            @handle-close="handleClose"
        >
            <template v-slot:default>

                <div class="protection-info">
                    <p class="protection-info--title">
                        {{ "quote-results.static.protection_header"| __ }}
                    </p>
                    <p class="protection-info--subtitle">
                        <div
                            v-if="quote.protection_levels.length > 0"
                            class="protections">
                            <p
                                v-for="level in quote.protection_levels"
                                :key="level.name"
                            >
                                <strong>
                                    {{ level.name }}
                                    {{ (level.name.length > 0) ? ":" : "" }}
                                </strong
                                >
                                {{ level.description }}
                            </p>
                        </div>
                    </p>
                </div>

            </template>
        </app-modal>

        <policy-details
            :currency="currency"
            :is-policy-details-open="isPolicyDetailsOpen"
            :quote-type="quoteType"
            :selected-quote="quote"
            @close-policy-details="handleClose"
            :policy-includes="policyIncludes"

        />

        <div class="container flex flex-col">
            <div class="payment-quote">
                <div class="message mb-3">
                    {{ $trans("quote-results.static.payment-title") }}
                </div>

                <div v-if="quote.summary_lines" class="payment-quote-info">
                    {{ quote.summary_lines.join(", ") }}
                </div>

                <app-quote-item
                    :hide-purchase="true"
                    :policyIncludesKeys="policyIncludesKeys"
                    :quote="quote"
                    :type="quoteType"
                    @details-click="handleDetailsClick(quote)"
                    @protection-click="handleProtectionClick(quote)"
                ></app-quote-item>
            </div>
            <div v-if="external" class="form-submit">
                <a :href="external" class="external-pay" target="_blank">
                    {{ "quote-results.static.external_pay" | __ }}
                </a>
            </div>

            <form
                v-if="!external"
                class="quote-form mt-auto"
                @submit.prevent="submit()"
            >
                <div class="payment-form">
                    <div class="message mb-10">
                        {{ $trans("quote-results.static.payment") }}
                    </div>

                    <div class="form-inputs form-inputs-pay">
                        <div class="form-row">
                            <div class="w-full md:flex-1">
                                <div class="app-input">
                                    <label>{{
                                            "quote-results.card.number-label" | __
                                        }}</label>
                                    <the-mask
                                        v-model="form.card_number"
                                        :mask="creditCardMask"
                                        :masked="false"
                                        :placeholder="
                                            $trans('quote-results.card.number')
                                        "
                                        dir="ltr"
                                        inputmode="numeric"
                                        style="text-align: right;"
                                        type="tel"
                                    />
                                </div>
                            </div>
                            <div class="form-row flex-1">
                                <div class="app-input">
                                    <label>{{
                                            "quote-results.card.expiry-label" | __
                                        }}</label>
                                    <the-mask
                                        v-model="form.card_expiry"
                                        :mask="'##/##'"
                                        :masked="false"
                                        :placeholder="
                                            $trans('quote-results.card.expiry')
                                        "
                                        inputmode="numeric"
                                        type="tel"
                                    />
                                </div>
                                <div class="app-input">
                                    <label>{{
                                            "quote-results.card.cvv-label" | __
                                        }}</label>
                                    <input
                                        v-model="form.card_cvv"
                                        v-mask="'###'"
                                        :placeholder="
                                            $trans('quote-results.card.cvv')
                                        "
                                        type="text"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="form-row">
                            <app-input
                                v-model="form.card_holder_name"
                                :error="cardNameInvalid"
                                :message="cardNameInvalid ? validation_full_name : ''"
                                :placeholder="
                                    $trans('quote-results.card.owner')
                                "
                                label="quote-results.card.owner"
                                type="text"
                            ></app-input>
                            <div class="app-input">
                                <label>{{
                                        "quote-results.card.owner_id" | __
                                    }}</label>
                                <the-mask
                                    v-model="form.card_holder_id"
                                    :mask="ownerIdMask"
                                    :masked="false"
                                    :placeholder="
                                        $trans('quote-results.card.owner_id')
                                    "
                                    dir="rtl"
                                    inputmode="numeric"
                                    type="tel"
                                />
                            </div>
                        </div>

                        <div class="form-row">
                            <div v-if="quoteType === 'car'" class="app-input">
                                <label class="tooltip-label">
                                    <span>{{
                                            "quote-results.card.payments-label-hova"
                                                | __
                                        }}</span>
                                    <v-tooltip
                                        :triggers="['hover', 'touch']"
                                        placement="bottom"
                                    >
                                        <img :src="infoIcon" alt="info-icon"/>
                                        <template #popper>
                                            {{ tooltipText }}
                                        </template>
                                    </v-tooltip>
                                </label>

                                <select v-model="form.paymentsHova">
                                    <option
                                        v-for="payment in quote.max_payments_hova"
                                        :key="payment"
                                        :value="payment"
                                    >{{ payment }}
                                    </option
                                    >
                                </select>
                            </div>
                            <div class="app-input" v-bind:class="{ 'w-1/2': quoteType === 'car' }">
                                <label class="tooltip-label">
                                    <span>{{
                                            $trans(
                                                `quote-results.card.payments-label-${quoteType === 'car'? quote.optionalCoverage : quoteType}`
                                            )
                                        }}</span>
                                    <v-tooltip
                                        :triggers="['hover', 'touch']"
                                        placement="bottom"
                                    >
                                        <img :src="infoIcon" alt="info-icon"/>
                                        <template #popper>
                                            {{ tooltipText }}
                                        </template>
                                    </v-tooltip>
                                </label>

                                <select v-model="form.payments">
                                    <option
                                        v-for="payment in quote.max_payments"
                                        :key="payment"
                                        :value="payment"
                                    >{{ payment }}
                                    </option
                                    >
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="form-submit mt-7">
                        <app-button
                            :disabled="!validateCardInfo"
                            :loading="loading"
                        >
                            {{ "quote-results.static.finish" | __ }}
                        </app-button>
                    </div>
                </div>
            </form>
        </div>

        <div :class="{ 'is-active': showSuccess }" class="success-payment">
            <div class="image">
                <svg
                    fill="none"
                    height="359"
                    viewBox="0 0 350 359"
                    width="350"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle
                        cx="174.648"
                        cy="183.423"
                        fill="white"
                        opacity="0.3"
                        r="174.648"
                    />
                    <g clip-path="url(#clip0_1065_1666)">
                        <path
                            clip-rule="evenodd"
                            d="M182.904 94.4512C198.18 94.4512 210.6 106.729 210.848 121.952C221.777 111.342 239.236 111.448 250.041 122.253C260.846 133.058 260.952 150.517 250.342 161.464C265.565 161.694 277.843 174.114 277.843 189.39C277.843 204.666 265.565 217.086 250.342 217.334C260.952 228.263 260.846 245.722 250.041 256.527C239.236 267.332 221.777 267.438 210.848 256.828C210.6 272.069 198.18 284.329 182.904 284.329C167.628 284.329 155.208 272.069 154.96 256.828C144.031 267.438 126.572 267.332 115.767 256.527C104.962 245.722 104.856 228.263 115.466 217.334C100.243 217.086 87.965 204.666 87.965 189.39C87.965 174.114 100.243 161.694 115.466 161.464C104.856 150.517 104.962 133.058 115.767 122.253C126.572 111.448 144.031 111.342 154.96 121.952C155.208 106.729 167.628 94.4512 182.904 94.4512ZM182.904 127.807C216.916 127.807 244.505 155.378 244.505 189.39C244.505 223.402 216.916 250.991 182.904 250.991C148.892 250.991 121.303 223.402 121.303 189.39C121.303 155.378 148.892 127.807 182.904 127.807Z"
                            fill="url(#paint0_linear_1065_1666)"
                            fill-rule="evenodd"
                        />
                        <path
                            clip-rule="evenodd"
                            d="M135.194 271.185L98.5034 342.58L123.981 333.745L131.273 358.318L166.9 286.87C159.271 282.86 153.327 276.065 150.417 267.814C145.627 270.121 140.393 271.239 135.194 271.185ZM230.612 271.185L267.303 342.58L241.825 333.745L234.533 358.318L198.907 286.87C206.536 282.86 212.48 276.065 215.389 267.814C220.18 270.121 225.414 271.239 230.612 271.185Z"
                            fill="url(#paint1_linear_1065_1666)"
                            fill-rule="evenodd"
                        />
                        <path
                            d="M183.549 156.683L192.379 175.33C193.17 177.001 194.758 178.155 196.592 178.391L217.055 181.028C217.53 181.089 217.72 181.674 217.372 182.002L202.366 196.162C201.021 197.432 200.415 199.298 200.757 201.116L204.573 221.392C204.661 221.862 204.164 222.223 203.744 221.994L185.641 212.098C184.018 211.211 182.055 211.211 180.432 212.098L162.328 221.994C161.909 222.224 161.411 221.862 161.499 221.392L165.314 201.115C165.655 199.298 165.049 197.432 163.704 196.162L148.701 182.002C148.353 181.674 148.543 181.089 149.017 181.028L169.48 178.391C171.315 178.155 172.903 177.001 173.694 175.33L182.524 156.683C182.729 156.251 183.344 156.251 183.549 156.683Z"
                            fill="white"
                            stroke="black"
                            stroke-width="4.86343"
                        />
                    </g>
                    <path
                        d="M294.878 62.2027L301.301 75.7679C302.066 77.3831 303.601 78.498 305.373 78.7264L320.26 80.6444C320.887 80.7253 321.139 81.499 320.678 81.9331L309.762 92.2345C308.462 93.4611 307.876 95.265 308.207 97.0214L310.983 111.772C311.1 112.394 310.442 112.872 309.887 112.568L296.717 105.369C295.149 104.512 293.252 104.512 291.683 105.369L278.513 112.569C277.958 112.872 277.3 112.394 277.417 111.773L280.191 97.0208C280.522 95.2647 279.936 93.4612 278.636 92.2347L267.721 81.9331C267.261 81.499 267.512 80.7254 268.14 80.6445L283.027 78.7264C284.799 78.498 286.334 77.3831 287.099 75.7679L293.523 62.2027C293.793 61.631 294.607 61.631 294.878 62.2027Z"
                        stroke="black"
                        stroke-width="4.49766"
                    />
                    <path
                        d="M237.156 29.4474L240.981 21.3697L244.807 29.4474C245.353 30.6012 246.449 31.3975 247.715 31.5607L256.58 32.7028L250.079 38.8371C249.151 39.7132 248.732 41.0017 248.968 42.2563L250.621 51.04L242.779 46.7531C241.659 46.1407 240.304 46.1407 239.184 46.7531L231.341 51.0403L232.993 42.2559C233.229 41.0015 232.81 39.7133 231.882 38.8373L225.383 32.7029L234.248 31.5607C235.514 31.3975 236.61 30.6012 237.156 29.4474Z"
                        stroke="black"
                        stroke-width="4.49766"
                    />
                    <path
                        clip-rule="evenodd"
                        d="M309.934 0L314.792 10.2602L326.052 11.711L317.795 19.5025L319.895 30.6594L309.934 25.2141L299.972 30.6594L302.071 19.5025L293.815 11.711L305.075 10.2602L309.934 0Z"
                        fill="url(#paint2_linear_1065_1666)"
                        fill-rule="evenodd"
                    />
                    <defs>
                        <linearGradient
                            id="paint0_linear_1065_1666"
                            gradientUnits="userSpaceOnUse"
                            x1="70.4313"
                            x2="300.936"
                            y1="189.39"
                            y2="189.39"
                        >
                            <stop stop-color="#185AF5"/>
                            <stop offset="1" stop-color="#18DAE7"/>
                        </linearGradient>
                        <linearGradient
                            id="paint1_linear_1065_1666"
                            gradientUnits="userSpaceOnUse"
                            x1="82.916"
                            x2="287.833"
                            y1="313.066"
                            y2="313.066"
                        >
                            <stop stop-color="#185AF5"/>
                            <stop offset="1" stop-color="#18DAE7"/>
                        </linearGradient>
                        <linearGradient
                            id="paint2_linear_1065_1666"
                            gradientUnits="userSpaceOnUse"
                            x1="290.839"
                            x2="329.973"
                            y1="15.3297"
                            y2="15.3297"
                        >
                            <stop stop-color="#185AF5"/>
                            <stop offset="1" stop-color="#18DAE7"/>
                        </linearGradient>
                        <clipPath id="clip0_1065_1666">
                            <rect
                                fill="white"
                                height="263.863"
                                transform="translate(50.9756 94.4512)"
                                width="263.863"
                            />
                        </clipPath>
                    </defs>
                </svg>
            </div>

            <div class="text">
                <p class="top">
                    {{ (isAgent ? "quote-results.static.success_title-agent" : "quote-results.static.success_title") | __ }}
                </p>
                <p class="bottom">
                    {{ (isAgent ? "quote-results.static.success_subtitle-agent" : "quote-results.static.success_subtitle") | __ }}
                </p>
            </div>

            <div class="description mt-5">
                {{ (isAgent ? "quote-results.static.success_description-agent" : "quote-results.static.success_description") | __ }}
            </div>

            <div class="actions">
                <a class="action account" href="/">
                    {{ (isAgent ? "quote-results.static.success_user-agent" : "quote-results.static.success_user") | __ }}
                </a>

                <a class="action homepage" href="https://www.surely.co.il/">
                    {{ "quote-results.static.success_home" | __ }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import QuoteProgress from "../QuoteSteps/Progress";
import infoIcon from "../../../images/icons/blueInfo.svg";
import {TheMask} from "vue-the-mask";
import {Tooltip as VTooltip} from "floating-vue";
import PolicyDetails from "./PolicyDetails.vue";

const masks = [
    {
        mask: "#### ###### #####",
        regex: "^3[47]\\d{0,13}",
        cardtype: "american express"
    },
    {
        mask: "#### #### #### ####",
        regex: "^(?:6011|65\\d{0,2}|64[4-9]\\d?)\\d{0,12}",
        cardtype: "discover"
    },
    {
        mask: "#### ###### ####",
        regex: "^3(?:0([0-5]|9)|[689]\\d?)\\d{0,11}",
        cardtype: "diners"
    },
    {
        mask: "#### #### #### ####",
        regex: "^(5[1-5]\\d{0,2}|22[2-9]\\d{0,1}|2[3-7]\\d{0,2})\\d{0,12}",
        cardtype: "mastercard"
    },
    {
        mask: "#### ###### #####",
        regex: "^(?:2131|1800)\\d{0,11}",
        cardtype: "jcb15"
    },
    {
        mask: "#### #### #### ####",
        regex: "^(?:35\\d{0,2})\\d{0,12}",
        cardtype: "jcb"
    },
    {
        mask: "#### #### #### ####",
        regex: "^(?:5[0678]\\d{0,2}|6304|67\\d{0,2})\\d{0,12}",
        cardtype: "maestro"
    },
    {
        mask: "#### #### #### ####",
        regex: "^4\\d{0,15}",
        cardtype: "visa"
    },
    {
        mask: "#### #### #### ####",
        regex: "^62\\d{0,14}",
        cardtype: "unionpay"
    }
];

export default {
    props: {
        quoteRequest: Object,
        external: String,
        quote: Object
    },

    components: {
        PolicyDetails,
        QuoteProgress,
        TheMask,
        VTooltip
    },

    data() {
        return {
            steps: window.yna.steps,
            step: window.yna.quote.step,
            quoteId: window.yna.quote.id,
            quoteRequestId: window.yna.quote.request_id,
            returnLink: window.yna.quote.index,
            quoteType: window.yna.quote.type,
            isAgent: window.yna.isAgent,
            firstName: "",
            showSuccess: false,
            loading: false,
            infoIcon,
            validation_full_name: "",
            form: {
                card_number: "",
                card_expiry: "",
                card_holder_name:  window.yna.form.personal.first_name + " " + window.yna.form.personal.last_name,
                card_cvv: "",
                card_holder_id: "",
                payments: 1,
                paymentsHova: 1
            },
            isPolicyDetailsOpen: false,
            isProtectionDetailsOpen: false,
            policyIncludesKeys: ["drag", "windowpane", "replace_car"],

        };
    },

    methods: {
        quoteStepPrev() {
            window.history.back();
        },
        async submit() {
            this.loading = true;
            let response = await this.$api.post(
                `pay/${this.quoteRequestId}/${this.quoteId}`,
                this.form
            );
            this.errors = response.errors();

            if (response.hasErrors() || !response.data().action) {
                this.$swal({
                    title: "שגיאה",
                    text: response.data().msg,
                    icon: "error",
                    button: "סגור"
                });
                this.loading = false;
                return;
            }

            this.firstName = response.data().firstName;
            this.showSuccess = true;
            this.$swal({
                title: "העסקה הושלמה בהצלחה",
                text: "תודה ".response.data().firstName,
                icon: "success",
                button: "סגור"
            });
            this.loading = false;
        },
        handleDetailsClick() {
            this.isPolicyDetailsOpen = true;
        },
        handleProtectionClick() {
            this.isProtectionDetailsOpen = true;
        },
        handleClose() {
            this.isPolicyDetailsOpen = false;
            this.isProtectionDetailsOpen = false;
        }
    },

    computed: {
        policyIncludes() {
            if (this.quote) {
                return this.quote.policyIncludes[this.quoteType];
            }

            return {};

        },
        cardNameInvalid() {
            const regex = /^[\u05D0-\u05EA\s]+$/;

            // Check if card_holder_name exists
            if (!this.form.card_holder_name) {
                this.validation_full_name = 'יש להקליד שם ושם מחשפחה'
                console.log('Card holder name does not exist.');
                return true;
            }

            // Check if card_holder_name matches the regex
            if (!regex.test(this.form.card_holder_name)) {
                this.validation_full_name = 'אותיות בעברית'

                console.log('Card holder name does not match the regex.');
                return true;
            }

            const [firstName, lastName] = this.form.card_holder_name.split(" ");

            // Check if both names are present
            if (!firstName || !lastName) {
                this.validation_full_name = 'חסר שם או שם משפחה'

                console.log('First name or last name is missing.');
                return true;
            }

            // If none of the conditions are met, return false
            return false;
        },
        validateCardInfo() {
            return (
                this.form.card_number &&
                this.form.card_expiry &&
                !this.cardNameInvalid &&
                this.form.card_cvv &&
                this.form.payments
            );
        },
        progressSteps() {
            return this.steps.filter(step => step.includeInCount === true);
        },
        creditCardMask() {
            for (let mask of masks) {
                if (new RegExp(mask.regex).test(this.form.card_number)) {
                    return mask.mask;
                }
            }
            return "#### #### #### ####";
        },
        ownerIdMask() {
            return "#########";
        },
        tooltipText() {
            return this.$trans("quote-results.card.payments-info");
        },
        currency() {
            const currencySign = {
                car: "₪",
                travel: "$",
                pet: "₪",
                home: "₪"
            };

            return currencySign[this.quoteType];
        },
    }
};
</script>

<style lang="scss" scoped>
.external-pay {
    display: block;
    width: 100px;
    background: $primary-blue;
    text-align: center;
    padding: 20px;
    border-radius: 20px;

    margin: 20px auto auto;
    text-decoration: none;
    color: $white;
}

.page-quote-payment {
    position: relative;
    display: flex;
    flex-direction: column;

    .quote-form {
        flex: 0;
        padding-bottom: 30px;
    }

    .form-row {
        display: flex;
        align-items: flex-end;
        justify-content: space-around;
        flex: 1;
        width: 100%;
        flex-direction: row;
        gap: 40px;

        @media screen and (max-width: $mobile) {
            flex-direction: column;
            gap: 25px;
        }

        .app-input {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;

            label {
                color: rgba(0, 0, 0, 0.6);
                text-align: right;
                font-size: 18px;
                font-weight: $font-normal;
                margin-bottom: 10px;

                &.tooltip-label {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                }
            }
        }

        &:not(:last-of-type) {
            margin-bottom: 25px;
        }
    }

    .card-number {
        @media screen and (max-width: $desktop) {
            order: 1;
        }
    }

    .card-owner {
        @media screen and (max-width: $desktop) {
            order: 2;
        }
    }

    .card-date {
        @media screen and (max-width: $desktop) {
            order: 3;
            width: 50% !important;
        }
    }

    .card-cvv {
        @media screen and (max-width: $desktop) {
            order: 4;
            width: 50% !important;
        }
    }

    .full-in-mobile {
        @media screen and (max-width: $desktop) {
            width: 100%;
        }
    }

    .success-payment {
        background-color: $white;
        z-index: 99;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        opacity: 0;
        transition: 0.3s ease-in-out;
        text-align: center;

        &.is-active {
            opacity: 1;
            pointer-events: all;
        }

        .text {
            margin-top: 50px;

            .top {
                color: $black;
                text-align: center;
                font-size: 65px;
                font-weight: $font-bold;
            }

            .bottom {
                width: 360px;
                margin: 20px auto;
                color: rgba(0, 0, 0, 0.4);
                text-align: center;
                font-size: 25px;
                font-weight: $font-normal;
            }
        }

        .description {
            margin: 20px auto;
            color: var(--blue, $primary-blue);
            text-align: center;
            font-size: 25px;
            font-weight: $font-bold;
        }

        .actions {
            display: flex;
            margin-top: 30px;
            justify-content: center;

            .action {
                width: 430px;
                height: 60px;
                font-size: 25px;
                font-weight: $font-bold;
                letter-spacing: normal;
                display: flex;
                align-items: center;
                justify-content: center;
                text-decoration: none;
                padding: 14px 104px;

                border-radius: 100px;
                background: #3457ee;
                box-shadow: 1px 7px 17px 0 rgba(52, 87, 238, 0.29);

                + .action {
                    margin-right: 42px;
                }

                &.account {
                    color: $white;
                    background-color: $primary-blue;
                }

                &.homepage {
                    border-radius: 100px;
                    border: 1px solid $cyan;
                    background: unset;
                    box-shadow: 1px 7px 17px 0 rgba(52, 87, 238, 0.29);
                    color: $cyan;
                    font-size: 25px;
                    font-weight: $font-bold;
                }
            }
        }
    }

    .message {
        font-size: $font-size-desktop;
        font-weight: bold;
        text-align: center;
        line-height: 1.32;
        color: $black;
    }

    .payment-form {
        margin-top: 60px;

        .form-inputs {
            display: flex;
            flex-direction: column;
            max-width: 970px;
            width: 100%;
            margin: 0 auto;

            &.form-inputs-pay {
                @media screen and (max-width: $desktop) {
                    display: block;
                }
                @media screen and (max-width: $mobile) {
                    margin: 0;
                }
            }

            input {
                position: relative;
                z-index: 1;
                display: block;
                width: 100%;
                font-size: 20px;
                font-weight: $font-light;
                color: $black;
                outline: none;
                height: 50px;
                padding: 15px 33px;
                border-radius: 100px;
                border: 1px solid rgba(33, 67, 146, 0.2);
                background: $lightest-gray;
            }

            select {
                position: relative;
                z-index: 1;
                display: block;
                width: 100%;
                font-size: 20px;
                font-weight: $font-light;
                color: $black;
                outline: none;
                height: 50px;
                padding: 10px 33px;
                border-radius: 100px;
                border: 1px solid rgba(33, 67, 146, 0.2);
                background: $lightest-gray;
            }
        }

        .form-submit {
            text-align: center;
        }
    }

    .payment-quote {
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: space-between;
        max-width: 1300px;
        width: 100%;
        margin: 0 auto;

        @media screen and (max-width: $desktop) {
            display: block;
        }

        .quote {
            margin-bottom: 0;
            width: 100%;
        }

        .payment-quote-info {
            color: rgba(0, 0, 0, 0.5);
            text-align: center;
            font-size: 25px;
            font-weight: $font-normal;
            margin-bottom: 60px;
        }
    }
}

.page-quotes {
    .page-quotes-prev {
        position: relative;
        padding: 0 137px;
    }

    .page-quotes-progress {
        max-width: 960px;
        margin: 0 auto;
        padding: 0 10px;
    }
}

.container {
    width: 100%;
    max-width: 1750px;
    margin: 0 auto;
    padding: 0 15px;
    height: 100%;
}

.v-popper__popper.v-popper__popper--show-from {
    .v-popper__wrapper {
        .v-popper__inner {
            background: #fff;
        }
    }
}

.v-popper--theme-tooltip {
    background: #fff;
}

.v-popper--theme-info-tooltip {
    background: #fff;
}

.v-popper--theme-dropdown .v-popper__inner {
    padding: 6px;
    background: #fff;
}

.quote-policy-details {

    .modal-body {
        border-radius: 15px;
        max-width: 600px;
        min-height: 250px;
        height: fit-content;
        max-height: 100%;
        overflow: auto;


        > div {
            margin-top: 20px;

            @media (max-width: $mobile) {
                margin-top: 0;
            }
        }

        .protection-info {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            flex: 1;
        }

        .protection-info > div {
            text-align: center;
        }

        .protection-info--title {
            color: var(--blue, $primary-blue);
            font-size: 35px;
            font-weight: $font-bold;
        }

        .protection-info--subtitle {
            color: $black;
            font-size: 18px;
            font-weight: $font-semi-bold;
        }

        .logo-container {
            display: flex;
            flex-direction: row;
            justify-content: center;
            width: 190px;
            border-radius: 70px;
            border: 1px solid $black;
            background: $white;
            height: 68px;
            margin: 10px auto;

            @media (max-width: $mobile) {
                width: 126px;
                height: 45px;

            }

            img {
                width: 70%;
                height: auto;
                object-fit: contain;
                object-position: center;
                padding: 10px;
                object-fit: cover;

                @media (max-width: $mobile) {
                    padding: 5px 0;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                }
            }
        }

        .price-container {
            @media (max-width: $mobile) {
                margin: 0 auto;
            }

            p {
                color: var(--blue, $primary-blue);
                font-size: $font-size-desktop;
                font-weight: $font-bold;
            }
        }

        .price-details-container {
            width: 100%;
            display: flex;
            justify-content: space-between;

            p {
                color: $black;
                text-align: center;
                font-size: 16px;

                span {
                    font-size: 20px;
                    font-weight: $font-bold;
                }
            }

            @media (max-width: $mobile) {
                p {
                    font-size: 14px;

                    span {
                        font-size: 14px;
                    }
                }
            }
        }

        .policy-include-container {
            display: flex;
            border-radius: 5px;
            background: linear-gradient(90deg, $primary-blue 0%, $cyan 100%);
            height: 70px;
            padding: 15px 35px;
            justify-content: space-around;
            align-content: center;
            color: #fff;

            @media (max-width: $mobile) {
                flex-direction: column;
                height: 100%;
                padding: 15px 55px;
            }

            .include-item {
                display: flex;
                justify-content: space-around;
                align-items: center;
                color: #fff;

                @media (max-width: $mobile) {
                    display: grid;
                    grid-template-columns: 2fr 1fr;
                    align-items: center;

                    &:first-of-type {
                        grid-template-columns: 100%;
                        text-align: center;

                        p {
                            justify-content: center;
                        }
                    }
                }


                p {
                    position: relative;
                    color: $white;
                    display: flex;
                    align-items: center;
                    font-size: 20px;
                    font-weight: $font-normal;

                    @media (max-width: $mobile) {
                        font-size: 15px;
                    }

                    // &:not(.no-mark) {
                    //     &:after {

                    //     }
                    // }

                }

                .check-icon {
                    display: block;
                    width: 30px;
                    background-image: url("data:image/svg+xml,%0A%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='Frame'%3E%3Cpath id='Vector' d='M8 12.4286L10.538 15L16 9' stroke='%2338DBBC' stroke-linecap='round'/%3E%3Cpath id='Vector_2' d='M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z' stroke='%2338DBBC'/%3E%3C/g%3E%3C/svg%3E ");
                    background-repeat: no-repeat;
                    height: 25px;
                }

            }
        }

        .policy-details-container {
            display: flex;
            flex-direction: column;

            > div {
                margin: 0 auto;
                width: 100%;
            }

            .policy-details-item {
                .title {
                    margin: 15px 0;
                    padding-bottom: 10px;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

                    p {
                        color: $black;
                        font-size: 20px;
                        font-weight: $font-semi-bold;

                        @media (max-width: $mobile) {
                            font-size: 15px;
                        }
                    }
                }

                .sub-title {
                    margin: 15px 0;
                    padding-bottom: 10px;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

                    p {
                        color: $black;
                        font-size: 20px;
                        font-weight: $font-normal;

                        @media (max-width: $mobile) {
                            font-size: 15px;
                        }
                    }

                    span {
                        color: $black;
                        font-size: 20px;
                        font-weight: $font-bold;

                        @media (max-width: $mobile) {
                            font-size: 15px;
                        }
                    }
                }
            }
        }

        .note {
            p {
                color: $error-red;
                text-align: right;
                font-size: 18px;
                font-weight: $font-semi-bold;

                @media (max-width: $mobile) {
                    font-size: 13px;
                }
            }
        }
    }
}
</style>
